import { SpinnerContainer } from "../../UI/Loading/SpinnerContainer.tsx";
import { BookmarkIcon } from "@heroicons/react/24/solid/index.js";
import { useRemainingLessonPerWeek } from "./useRemainingLessonPerWeek.ts";
import { useAllYouCanSpeakBonusStatus } from "./useAllYouCanSpeakBonusStatus.ts";
import { Interval } from "../../Speaking/Interval.tsx";
import { Trans, useTranslation } from "react-i18next";
import { TI } from "../../I18n/TI.ts";
import { LoaderSize } from "@normasteaching/norma-ui";
import { useShouldHideGroupSections } from "../useShouldHideGroupSections.ts";

type Props = {
  interval: Interval;
};
export function WeekRemainingLessons({ interval }: Props) {
  const {
    remainingLessonCount,
    totalLessonCount,
    loading: loadingRemainingLessons,
  } = useRemainingLessonPerWeek({
    date: interval.fromDate,
  });

  const { isBonusActive, freeBookingsUnlocked, lessonsCountedPerWeek } =
    useAllYouCanSpeakBonusStatus({
      date: interval.fromDate,
    });

  const { t } = useTranslation("weeklyRemainingLessons");

  const { shouldHide } = useShouldHideGroupSections();

  const isUnlimitedPeriodActive =
    isBonusActive &&
    freeBookingsUnlocked &&
    (lessonsCountedPerWeek === 0 || lessonsCountedPerWeek == null);

  if (shouldHide) return null;

  if (isUnlimitedPeriodActive) {
    return (
      <div className="relative group flex-shrink-0">
        <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full cursor-default whitespace-nowrap">
          All You Can Speak!
        </span>
        <div className="absolute top-full right-0 mt-2 w-72 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
          All You Can Speak attivo! Puoi prenotare quante lezioni vuoi fino a
          domenica senza consumare il tuo pacchetto.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end space-y-2">
      {isBonusActive && !freeBookingsUnlocked && lessonsCountedPerWeek && (
        <div className="flex items-start space-x-2 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-900 max-w-sm">
          <span className="text-lg leading-none flex-shrink-0">🎉</span>
          <p>
            <span className="font-semibold">
              Sei nel periodo All You Can Speak!
            </span>{" "}
            Prenota {lessonsCountedPerWeek} lezioni questa settimana e potrai
            prenotarne quante vuoi fino a domenica.
          </p>
        </div>
      )}
      <SpinnerContainer
        size={LoaderSize.Small}
        loading={loadingRemainingLessons}
      >
        <div className="shadow-slate-100/70 border border-primary shadow-lg px-5 py-2 flex items-center space-x-2 bg-white rounded-lg w-fit">
          <BookmarkIcon className="w-5 flex-shrink-0" />
          <span className="flex-1">
            {loadingRemainingLessons ? (
              t("remainingLessonsLoading")
            ) : (
              <Trans ns={"weeklyRemainingLessons"} i18nKey={"remainingLessons"}>
                Prenotazioni settimanali rimanenti:{" "}
                <b>
                  {{ remainingLessonCount } as TI} di{" "}
                  {{ totalLessonCount } as TI}
                </b>
              </Trans>
            )}
          </span>
          {isBonusActive && freeBookingsUnlocked && (
            <div className="relative group flex-shrink-0">
              <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full cursor-default whitespace-nowrap">
                All You Can Speak!
              </span>
              <div className="absolute top-full right-0 mt-2 w-72 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg">
                All You Can Speak attivo! Hai completato le lezioni conteggiate
                questa settimana. Puoi prenotare quante lezioni vuoi fino a
                domenica senza consumare il tuo pacchetto.
              </div>
            </div>
          )}
        </div>
      </SpinnerContainer>
    </div>
  );
}
